import { lazy, Suspense } from "react";
import { Toaster } from "@/modules/shared/components/ui/toaster";
import { Toaster as Sonner } from "@/modules/shared/components/ui/sonner";
import { TooltipProvider } from "@/modules/shared/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppLayout } from "@/modules/shared/components/layout/AppLayout";
import { ProtectedRoute } from "@/modules/shared/components/layout/ProtectedRoute";
import { MarketingRoute, AppRoute } from "@/modules/shared/components/DomainRouter";
import { ModuleAccessProvider } from "@/modules/shared/hooks/useModuleAccess";
import { ModuleGuard } from "@/modules/shared/components/ModuleGuard";
import { PortalAuthProvider } from "@/modules/portal/hooks/usePortalAuth";
import { AuthProvider } from "@/modules/shared/hooks/useAuth";
import { ChampionshipTenantProvider } from "@/modules/talent/contexts/ChampionshipTenantContext";
import { OrganizationProvider } from "@/modules/shared/hooks/useOrganization";
import { AllocationRedirect } from "@/modules/operations/components/allocations/AllocationRedirect";
import { SentryErrorBoundary } from "@/modules/shared/lib/observability";

// ── Core / always-needed ──────────────────────────────────────────────────
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const OpencodeLanding = lazy(() => import("./pages/OpencodeLanding"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Settings = lazy(() => import("./pages/Settings"));

// ── Marketplace ───────────────────────────────────────────────────────────
const ModuleCheckout = lazy(() => import("./modules/shared/pages/ModuleCheckout"));
const MyModules = lazy(() => import("./modules/shared/pages/MyModules"));

// ── Admin ─────────────────────────────────────────────────────────────────
const AdminDashboard = lazy(() => import("./modules/admin/pages/AdminDashboard"));
const AdminTenantDetail = lazy(() => import("./modules/admin/pages/AdminTenantDetail"));
const AdminMarketplace = lazy(() => import("./modules/admin/pages/AdminMarketplace"));

// ── Recruitment module ────────────────────────────────────────────────────
const Recruitment = lazy(() => import("./modules/talent/pages/Recruitment"));
const AssessmentDetail = lazy(() => import("./modules/talent/pages/AssessmentDetail"));
const CandidateDetail = lazy(() => import("./modules/talent/pages/CandidateDetail"));
const JobOpenings = lazy(() => import("./modules/talent/pages/JobOpenings"));
const JobOpeningOnBehalf = lazy(() => import("./modules/talent/pages/JobOpeningOnBehalf"));
const JobPipeline = lazy(() => import("./modules/talent/pages/JobPipeline"));
const ATSSettings = lazy(() => import("./modules/talent/pages/ATSSettings"));
const JobTemplates = lazy(() => import("./modules/talent/pages/JobTemplates"));
const JobTemplateEdit = lazy(() => import("./modules/talent/pages/JobTemplateEdit"));
const JobApplication = lazy(() => import("./modules/talent/pages/JobApplication"));
const CareerPageEditor = lazy(() => import("./modules/talent/pages/CareerPageEditor"));
const CandidateRegistration = lazy(() => import("./modules/talent/pages/CandidateRegistration"));
const CandidateAssessment = lazy(() => import("./modules/talent/pages/CandidateAssessment"));
const OfferAcceptance = lazy(() => import("./modules/talent/pages/OfferAcceptance"));
const CandidateShareView = lazy(() => import("./modules/talent/pages/CandidateShareView"));

// ── Squad / OaaS module ───────────────────────────────────────────────────
const Squads = lazy(() => import("./modules/operations/pages/Squads"));
const SquadContractDetail = lazy(() => import("./modules/operations/pages/SquadContractDetail"));
const SquadGlobalOperations = lazy(() => import("./modules/operations/pages/SquadGlobalOperations"));
const Projects = lazy(() => import("./modules/operations/pages/Projects"));
const ProjectDetail = lazy(() => import("./modules/operations/pages/ProjectDetail"));
const SpecQAProjects = lazy(() => import("./modules/operations/pages/SpecQAProjects"));
const SpecQAProjectDetail = lazy(() => import("./modules/operations/pages/SpecQAProjectDetail"));

// ── Championship module ───────────────────────────────────────────────────
const ChampionshipLanding = lazy(() => import("./modules/talent/pages/championship/ChampionshipLanding"));
const ChampionshipRegistration = lazy(() => import("./modules/talent/pages/championship/ChampionshipRegistration"));
const ChampionshipConfirmation = lazy(() => import("./modules/talent/pages/championship/ChampionshipConfirmation"));
const ChampionshipLogin = lazy(() => import("./modules/talent/pages/championship/ChampionshipLogin"));
const ChampionshipDashboard = lazy(() => import("./modules/talent/pages/championship/ChampionshipDashboard"));
const ChampionshipCompetitorProfile = lazy(() => import("./modules/talent/pages/championship/ChampionshipCompetitorProfile"));
const ChampionshipQualification = lazy(() => import("./modules/talent/pages/championship/ChampionshipQualification"));
const ChampionshipLeaderboard = lazy(() => import("./modules/talent/pages/championship/ChampionshipLeaderboard"));
const ChampionshipBracket = lazy(() => import("./modules/talent/pages/championship/ChampionshipBracket"));
const ChampionshipDuel = lazy(() => import("./modules/talent/pages/championship/ChampionshipDuel"));
const ChampionshipDuelResult = lazy(() => import("./modules/talent/pages/championship/ChampionshipDuelResult"));
const ChampionshipAdmin = lazy(() => import("./modules/talent/pages/championship/ChampionshipAdmin"));
const ChampionshipLive = lazy(() => import("./modules/talent/pages/championship/ChampionshipLive"));
const ChampionshipOverlay = lazy(() => import("./modules/talent/pages/championship/ChampionshipOverlay"));
const ChampionshipRegulamento = lazy(() => import("./modules/talent/pages/championship/ChampionshipRegulamento"));
const ChampionshipMediaKit = lazy(() => import("./modules/talent/pages/championship/ChampionshipMediaKit"));

// ── Portal (client-facing) module ─────────────────────────────────────────
const ClientPortalAuth = lazy(() => import("./modules/portal/pages/ClientPortalAuth"));
const ClientPortalDashboard = lazy(() => import("./modules/portal/pages/ClientPortalDashboard"));
const ClientPortalActionPlans = lazy(() => import("./modules/portal/pages/ClientPortalActionPlans"));
const ClientPortalResources = lazy(() => import("./modules/portal/pages/ClientPortalResources"));
const ClientPortalJobOpening = lazy(() => import("./modules/portal/pages/ClientPortalJobOpening"));
const ClientPortalStack = lazy(() => import("./modules/portal/pages/ClientPortalStack"));
const ClientPortalMatch = lazy(() => import("./modules/portal/pages/ClientPortalMatch"));
const ClientPortalPerformance = lazy(() => import("./modules/portal/pages/ClientPortalPerformance"));
const ClientPortalTimeline = lazy(() => import("./modules/portal/pages/ClientPortalTimeline"));
const ClientPortalRisks = lazy(() => import("./modules/portal/pages/ClientPortalRisks"));
const ClientPortalSummary = lazy(() => import("./modules/portal/pages/ClientPortalSummary"));
const ClientPortalCheckin = lazy(() => import("./modules/portal/pages/ClientPortalCheckin"));
const ClientPortalProfessionalDetail = lazy(() => import("./modules/portal/pages/ClientPortalProfessionalDetail"));

// ── Public pages ──────────────────────────────────────────────────────────
const PublicJobsList = lazy(() => import("./modules/talent/pages/public/PublicJobsList"));
const Pricing = lazy(() => import("./pages/Pricing"));
const PublicJobDetail = lazy(() => import("./modules/talent/pages/public/PublicJobDetail"));
const PublicJobApply = lazy(() => import("./modules/talent/pages/public/PublicJobApply"));
const CareersLanding = lazy(() => import("./modules/talent/pages/public/CareersLanding"));
const PublicCareersLanding = lazy(() => import("./modules/talent/pages/public/PublicCareersLanding"));
const PublicJobsListByOrg = lazy(() => import("./modules/talent/pages/public/PublicJobsListByOrg"));
const PublicNPSSurvey = lazy(() => import("./modules/talent/pages/public/PublicNPSSurvey"));
const CandidatePortal = lazy(() => import("./modules/talent/pages/public/CandidatePortal"));
const LectureRegistration = lazy(() => import("./modules/talent/pages/public/LectureRegistration"));

// ── People / HR module ────────────────────────────────────────────────────
const Clients = lazy(() => import("./modules/sales/pages/Clients"));
const ClientDetail = lazy(() => import("./modules/sales/pages/ClientDetail"));
const Professionals = lazy(() => import("./modules/talent/pages/Professionals"));
const ProfessionalProfile = lazy(() => import("./modules/talent/pages/ProfessionalProfile"));
const Allocations = lazy(() => import("./modules/talent/pages/Allocations"));
const AllocationHistory = lazy(() => import("./modules/talent/pages/AllocationHistory"));
const Checkins = lazy(() => import("./modules/talent/pages/Checkins"));
const CheckinOnBehalf = lazy(() => import("./modules/talent/pages/CheckinOnBehalf"));
const CheckinEdit = lazy(() => import("./modules/talent/pages/CheckinEdit"));
const ActionPlans = lazy(() => import("./modules/talent/pages/ActionPlans"));
const MonthlySummary = lazy(() => import("./modules/talent/pages/MonthlySummary"));
const PDI = lazy(() => import("./modules/talent/pages/PDI"));
const PDISessionDetail = lazy(() => import("./modules/talent/pages/PDISessionDetail"));
const CultureDashboard = lazy(() => import("./modules/talent/pages/CultureDashboard"));
const CultureConfig = lazy(() => import("./modules/talent/pages/CultureConfig"));
const EngagementDashboard = lazy(() => import("./modules/talent/pages/EngagementDashboard"));
const Reimbursements = lazy(() => import("./modules/talent/pages/Reimbursements"));
const LectureRegistrations = lazy(() => import("./modules/talent/pages/LectureRegistrations"));
const ClientNPSSurvey = lazy(() => import("./modules/sales/pages/ClientNPSSurvey"));

// ── Commercial / Finance module ───────────────────────────────────────────
const Financial = lazy(() => import("./modules/sales/pages/Financial"));
const MyPortfolio = lazy(() => import("./modules/sales/pages/MyPortfolio"));
const RateCard = lazy(() => import("./modules/sales/pages/RateCard"));
const CommercialCalculator = lazy(() => import("./modules/sales/pages/CommercialCalculator"));
const CommercialForecast = lazy(() => import("./modules/sales/pages/CommercialForecast"));
const RevenueIntelligence = lazy(() => import("./modules/sales/pages/RevenueIntelligence"));
const Pipeline = lazy(() => import("./modules/sales/pages/Pipeline"));
const PipelineSettings = lazy(() => import("./modules/sales/pages/PipelineSettings"));
const AccountView = lazy(() => import("./modules/sales/pages/AccountView"));

// ── Timesheet module ──────────────────────────────────────────────────────
const TimesheetMyTimes = lazy(() => import("./modules/operations/pages/timesheet/TimesheetMyTimes"));
const TimesheetWeekly = lazy(() => import("./modules/operations/pages/timesheet/TimesheetWeekly"));
const TimesheetCalendar = lazy(() => import("./modules/operations/pages/timesheet/TimesheetCalendar"));
const TimesheetAllTimes = lazy(() => import("./modules/operations/pages/timesheet/TimesheetAllTimes"));

// ─────────────────────────────────────────────────────────────────────────────

const queryClient = new QueryClient();

const ErrorFallback = () => {
  // Generates a short reference id so the user can mention it when
  // reporting the issue. Stable for the lifetime of the page.
  const ref = `OPH-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        background:
          "radial-gradient(circle at top, rgba(198,240,76,0.15) 0%, rgba(251,250,244,1) 38%, #f4f3ec 100%)",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        color: "#0b0d0b",
      }}
    >
      <div
        style={{
          maxWidth: 460,
          width: "100%",
          background: "#ffffff",
          border: "1px solid #e9e6da",
          borderRadius: 20,
          padding: "40px 36px 32px",
          boxShadow: "0 24px 60px -28px rgba(11,13,11,0.18), 0 8px 18px -12px rgba(11,13,11,0.08)",
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: "#fff7ed",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 20,
          }}
          aria-hidden
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <span
          style={{
            display: "inline-block",
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "#6b716b",
            marginBottom: 10,
          }}
        >
          Algo saiu do roteiro
        </span>
        <h1
          style={{
            fontFamily: "'Reckless Neue', 'Times New Roman', serif",
            fontSize: 32,
            lineHeight: 1.1,
            margin: 0,
            marginBottom: 12,
            fontWeight: 600,
          }}
        >
          Tropeçamos numa <em style={{ fontStyle: "italic", color: "#4964A2" }}>esquina inesperada</em>.
        </h1>
        <p style={{ color: "#4a4f4a", fontSize: 15, lineHeight: 1.55, marginBottom: 24 }}>
          A página não carregou direito. Já registramos o erro internamente — geralmente recarregar resolve.
          Se persistir, fale com o suporte e cite o código abaixo.
        </p>
        <div
          style={{
            background: "#f3f1e7",
            border: "1px solid #e9e6da",
            borderRadius: 12,
            padding: "10px 14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 24,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 12,
          }}
        >
          <span style={{ color: "#6b716b" }}>Código de referência</span>
          <span style={{ fontWeight: 600, letterSpacing: "0.04em" }}>{ref}</span>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={() => window.location.reload()}
            style={{
              flex: 1,
              padding: "12px 20px",
              background: "#c6f04c",
              color: "#0b0d0b",
              border: "none",
              borderRadius: 12,
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 14,
              boxShadow: "0 1px 0 rgba(11,13,11,0.06)",
              transition: "transform 0.12s ease",
            }}
            onMouseDown={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = "scale(0.98)"; }}
            onMouseUp={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = "scale(1)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = "scale(1)"; }}
          >
            Recarregar página
          </button>
          <button
            onClick={() => { window.location.href = "/"; }}
            style={{
              padding: "12px 20px",
              background: "transparent",
              color: "#0b0d0b",
              border: "1px solid #e9e6da",
              borderRadius: 12,
              cursor: "pointer",
              fontWeight: 500,
              fontSize: 14,
            }}
          >
            Ir para o início
          </button>
        </div>
        <p style={{ marginTop: 22, marginBottom: 0, fontSize: 12, color: "#a8ada8", textAlign: "center" }}>
          Se o problema continuar, copie o código <strong style={{ color: "#6b716b", fontWeight: 600 }}>{ref}</strong> e abra um chamado no suporte.
        </p>
      </div>
    </div>
  );
};

const App = () => (
  <SentryErrorBoundary fallback={<ErrorFallback />}>
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <ModuleAccessProvider>
      <PortalAuthProvider>
        <OrganizationProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <Suspense fallback={null}>
                <Routes>
                  {/* Landing Page — only on www/root domain */}
                  <Route path="/" element={<MarketingRoute><LandingPage /></MarketingRoute>} />
                  <Route path="/landing" element={<MarketingRoute><LandingPage /></MarketingRoute>} />
                  <Route path="/opencode" element={<MarketingRoute><OpencodeLanding /></MarketingRoute>} />

                  {/* Auth Routes — on app subdomain */}
                  <Route path="/auth" element={<AppRoute><Auth /></AppRoute>} />
                  <Route path="/reset-password" element={<AppRoute><ResetPassword /></AppRoute>} />
                  <Route path="/accept-invite" element={<AppRoute><AcceptInvite /></AppRoute>} />

                  {/* Pricing — public route */}
                  <Route path="/pricing" element={<MarketingRoute><Pricing /></MarketingRoute>} />

                  {/* Marketplace — protected routes */}
                  <Route path="/checkout" element={<AppLayout><ProtectedRoute><ModuleCheckout /></ProtectedRoute></AppLayout>} />
                  <Route path="/configuracoes/modulos" element={<AppLayout><ProtectedRoute><MyModules /></ProtectedRoute></AppLayout>} />

                  {/* Client Portal Routes */}
                  <Route path="/portal/login" element={<ClientPortalAuth />} />
                  <Route path="/portal" element={<AppLayout><ClientPortalDashboard /></AppLayout>} />
                  <Route path="/portal/planos" element={<AppLayout><ClientPortalActionPlans /></AppLayout>} />
                  <Route path="/portal/recursos" element={<AppLayout><ClientPortalResources /></AppLayout>} />
                  <Route path="/portal/vagas" element={<AppLayout><ClientPortalJobOpening /></AppLayout>} />
                  <Route path="/portal/stack" element={<AppLayout><ClientPortalStack /></AppLayout>} />
                  <Route path="/portal/match" element={<AppLayout><ClientPortalMatch /></AppLayout>} />
                  <Route path="/portal/performance" element={<AppLayout><ClientPortalPerformance /></AppLayout>} />
                  <Route path="/portal/timeline" element={<AppLayout><ClientPortalTimeline /></AppLayout>} />
                  <Route path="/portal/riscos" element={<AppLayout><ClientPortalRisks /></AppLayout>} />
                  <Route path="/portal/resumo" element={<AppLayout><ClientPortalSummary /></AppLayout>} />
                  <Route path="/portal/checkin" element={<ClientPortalCheckin />} />
                  <Route path="/portal/profissional/:allocationId" element={<AppLayout><ClientPortalProfessionalDetail /></AppLayout>} />

                  {/* Shell — always available */}
                  <Route path="/dashboard" element={<AppLayout><ProtectedRoute><Dashboard /></ProtectedRoute></AppLayout>} />
                  <Route path="/configuracoes" element={<AppLayout><ProtectedRoute><Settings /></ProtectedRoute></AppLayout>} />
                  <Route path="/assinatura" element={<Navigate to="/" replace />} />

                  {/* Sales Module */}
                  <Route path="/clientes" element={<AppLayout><ProtectedRoute><ModuleGuard module="sales"><Clients /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/clientes/:id" element={<AppLayout><ProtectedRoute><ModuleGuard module="sales"><ClientDetail /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/clientes/:clientId/nps" element={<AppLayout><ProtectedRoute><ModuleGuard module="sales"><ClientNPSSurvey /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/rate-card" element={<AppLayout><ProtectedRoute><ModuleGuard module="sales"><RateCard /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/financeiro" element={<AppLayout><ProtectedRoute><ModuleGuard module="sales"><Financial /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/minha-carteira" element={<AppLayout><ProtectedRoute><ModuleGuard module="sales"><MyPortfolio /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/calculadora-comercial" element={<AppLayout><ProtectedRoute><ModuleGuard module="sales"><CommercialCalculator /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/comercial/forecast" element={<AppLayout><ProtectedRoute><ModuleGuard module="sales"><CommercialForecast /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/revenue" element={<AppLayout><ProtectedRoute><ModuleGuard module="sales"><RevenueIntelligence /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/comercial/pipeline" element={<AppLayout><ProtectedRoute><ModuleGuard module="sales"><Pipeline /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/comercial/pipeline/configuracoes" element={<AppLayout><ProtectedRoute><ModuleGuard module="sales"><PipelineSettings /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/comercial/contas/:id" element={<AppLayout><ProtectedRoute><ModuleGuard module="sales"><AccountView /></ModuleGuard></ProtectedRoute></AppLayout>} />

                  {/* Talent Module */}
                  <Route path="/profissionais" element={<AppLayout><ProtectedRoute><ModuleGuard module="talent"><Professionals /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/profissionais/:id" element={<AppLayout><ProtectedRoute><ModuleGuard module="talent"><ProfessionalProfile /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/alocacoes" element={<Navigate to="/profissionais?tab=alocacoes" replace />} />
                  <Route path="/alocacoes/:id" element={<AppLayout><ProtectedRoute><ModuleGuard module="talent"><AllocationRedirect /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/historico-alocacoes" element={<AppLayout><ProtectedRoute><ModuleGuard module="talent"><AllocationHistory /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/checkins" element={<AppLayout><ProtectedRoute><ModuleGuard module="talent"><Checkins /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/checkins/novo" element={<ProtectedRoute><ModuleGuard module="talent"><CheckinOnBehalf /></ModuleGuard></ProtectedRoute>} />
                  <Route path="/checkins/editar/:id" element={<ProtectedRoute><ModuleGuard module="talent"><CheckinEdit /></ModuleGuard></ProtectedRoute>} />
                  <Route path="/planos-de-acao" element={<AppLayout><ProtectedRoute><ModuleGuard module="talent"><ActionPlans /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/pdi" element={<AppLayout><ProtectedRoute><ModuleGuard module="talent"><PDI /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/pdi/:sessionId" element={<AppLayout><ProtectedRoute><ModuleGuard module="talent"><PDISessionDetail /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/resumo-mensal" element={<AppLayout><ProtectedRoute><ModuleGuard module="talent"><MonthlySummary /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/dashboard-engajamento" element={<AppLayout><ProtectedRoute><ModuleGuard module="talent"><EngagementDashboard /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/cultura" element={<AppLayout><ProtectedRoute><ModuleGuard module="talent"><CultureDashboard /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/cultura/configurar" element={<AppLayout><ProtectedRoute><ModuleGuard module="talent"><CultureConfig /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/reembolsos" element={<AppLayout><ProtectedRoute><ModuleGuard module="talent"><Reimbursements /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/vagas" element={<AppLayout><ProtectedRoute><ModuleGuard module="talent"><JobOpenings /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/vagas/configuracoes" element={<AppLayout><ProtectedRoute><ModuleGuard module="talent"><ATSSettings /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/vagas/modelos" element={<AppLayout><ProtectedRoute><ModuleGuard module="talent"><JobTemplates /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/vagas/modelos/:id" element={<AppLayout><ProtectedRoute><ModuleGuard module="talent"><JobTemplateEdit /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/vagas/nova" element={<AppLayout><ProtectedRoute><ModuleGuard module="talent"><JobOpeningOnBehalf /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/vagas/:jobId/pipeline" element={<AppLayout><ProtectedRoute><ModuleGuard module="talent"><JobPipeline /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/vagas/:jobId/candidato/:candidateId" element={<AppLayout><ProtectedRoute><ModuleGuard module="talent"><CandidateDetail /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/recrutamento" element={<AppLayout><ProtectedRoute><ModuleGuard module="talent"><Recruitment /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/recrutamento/:id" element={<AppLayout><ProtectedRoute><ModuleGuard module="talent"><AssessmentDetail /></ModuleGuard></ProtectedRoute></AppLayout>} />

                  {/* Operations Module */}
                  <Route path="/squads" element={<AppLayout><ProtectedRoute><ModuleGuard module="operations"><Squads /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/squads/:id" element={<AppLayout><ProtectedRoute><ModuleGuard module="operations"><SquadContractDetail /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/open-as-service" element={<Navigate to="/squads" replace />} />
                  <Route path="/open-as-service/:id" element={<Navigate to="/squads" replace />} />
                  <Route path="/squad" element={<Navigate to="/squads" replace />} />
                  <Route path="/squad-operacoes" element={<AppLayout><ProtectedRoute><ModuleGuard module="operations"><SquadGlobalOperations /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/timesheet" element={<AppLayout><ProtectedRoute><ModuleGuard module="operations"><TimesheetMyTimes /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/timesheet/semanal" element={<AppLayout><ProtectedRoute><ModuleGuard module="operations"><TimesheetWeekly /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/timesheet/calendario" element={<AppLayout><ProtectedRoute><ModuleGuard module="operations"><TimesheetCalendar /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/timesheet/all-times" element={<AppLayout><ProtectedRoute><ModuleGuard module="operations"><TimesheetAllTimes /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/projetos" element={<AppLayout><ProtectedRoute><ModuleGuard module="operations"><Projects /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/projetos/:id" element={<AppLayout><ProtectedRoute><ModuleGuard module="operations"><ProjectDetail /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/specqa" element={<AppLayout><ProtectedRoute><ModuleGuard module="operations"><SpecQAProjects /></ModuleGuard></ProtectedRoute></AppLayout>} />
                  <Route path="/specqa/:id" element={<AppLayout><ProtectedRoute><ModuleGuard module="operations"><SpecQAProjectDetail /></ModuleGuard></ProtectedRoute></AppLayout>} />

                  {/* Admin */}
                  <Route path="/admin" element={<Navigate to="/admin/painel" replace />} />
                  <Route path="/admin/painel" element={<AppLayout><AdminDashboard /></AppLayout>} />
                  <Route path="/admin/empresas" element={<Navigate to="/admin/painel" replace />} />
                  <Route path="/admin/empresas/:id" element={<AppLayout><AdminTenantDetail /></AppLayout>} />
                  <Route path="/admin/modulos" element={<AppLayout><ProtectedRoute><AdminMarketplace /></ProtectedRoute></AppLayout>} />

                  {/* Career Page Editor */}
                  <Route path="/carreiras/editor" element={<AppLayout><ProtectedRoute><CareerPageEditor /></ProtectedRoute></AppLayout>} />

                  {/* Public Job Application (legacy) */}
                  <Route path="/candidatar/:jobId" element={<JobApplication />} />

                  {/* Public Jobs Pages */}
                  <Route path="/carreiras" element={<CareersLanding />} />
                  <Route path="/carreiras/:orgSlug" element={<PublicCareersLanding />} />
                  <Route path="/carreiras/:orgSlug/jobs" element={<PublicJobsListByOrg />} />
                  <Route path="/carreiras/:orgSlug/jobs/:slug" element={<PublicJobDetail />} />
                  <Route path="/carreiras/:orgSlug/jobs/:slug/apply" element={<PublicJobApply />} />
                  <Route path="/jobs" element={<PublicJobsList />} />
                  <Route path="/jobs/:slug" element={<PublicJobDetail />} />
                  <Route path="/jobs/:slug/apply" element={<PublicJobApply />} />
                  <Route path="/candidato/portal" element={<CandidatePortal />} />

                  {/* Candidate Assessment Portal (Public) */}
                  <Route path="/avaliacao/nova" element={<CandidateRegistration />} />
                  <Route path="/avaliacao/:token" element={<CandidateAssessment />} />

                  {/* Offer Acceptance (Public, token-gated) */}
                  <Route path="/proposta/:token" element={<OfferAcceptance />} />

                  {/* Candidate Share View (Public, token-gated) */}
                  <Route path="/candidato/partilhar/:token" element={<CandidateShareView />} />

                  {/* OpenCode Championship — main instance */}
                  <Route path="/opencode" element={<ChampionshipTenantProvider><ChampionshipLanding /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/inscricao" element={<ChampionshipTenantProvider><ChampionshipRegistration /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/login" element={<ChampionshipTenantProvider><ChampionshipLogin /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/dashboard" element={<ChampionshipTenantProvider><ChampionshipDashboard /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/competidor/:nickname" element={<ChampionshipTenantProvider><ChampionshipCompetitorProfile /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/confirmacao" element={<ChampionshipTenantProvider><ChampionshipConfirmation /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/qualificacao" element={<ChampionshipTenantProvider><ChampionshipQualification /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/qualificacao/ranking" element={<ChampionshipTenantProvider><ChampionshipLeaderboard /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/chaves" element={<ChampionshipTenantProvider><ChampionshipBracket /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/duelo/:id" element={<ChampionshipTenantProvider><ChampionshipDuel /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/duelo/:id/resultado" element={<ChampionshipTenantProvider><ChampionshipDuelResult /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/admin" element={<AppLayout><ProtectedRoute><ChampionshipTenantProvider><ChampionshipAdmin /></ChampionshipTenantProvider></ProtectedRoute></AppLayout>} />
                  <Route path="/opencode/ao-vivo" element={<ChampionshipTenantProvider><ChampionshipLive /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/overlay/:dueloId" element={<ChampionshipTenantProvider><ChampionshipOverlay /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/regulamento" element={<ChampionshipTenantProvider><ChampionshipRegulamento /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/media-kit" element={<ChampionshipTenantProvider><ChampionshipMediaKit /></ChampionshipTenantProvider>} />

                  {/* OpenCode Championship — tenant instance (with slug) */}
                  <Route path="/opencode/:slug" element={<ChampionshipTenantProvider><ChampionshipLanding /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/:slug/inscricao" element={<ChampionshipTenantProvider><ChampionshipRegistration /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/:slug/login" element={<ChampionshipTenantProvider><ChampionshipLogin /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/:slug/dashboard" element={<ChampionshipTenantProvider><ChampionshipDashboard /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/:slug/competidor/:nickname" element={<ChampionshipTenantProvider><ChampionshipCompetitorProfile /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/:slug/confirmacao" element={<ChampionshipTenantProvider><ChampionshipConfirmation /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/:slug/qualificacao" element={<ChampionshipTenantProvider><ChampionshipQualification /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/:slug/qualificacao/ranking" element={<ChampionshipTenantProvider><ChampionshipLeaderboard /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/:slug/chaves" element={<ChampionshipTenantProvider><ChampionshipBracket /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/:slug/duelo/:id" element={<ChampionshipTenantProvider><ChampionshipDuel /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/:slug/duelo/:id/resultado" element={<ChampionshipTenantProvider><ChampionshipDuelResult /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/:slug/ao-vivo" element={<ChampionshipTenantProvider><ChampionshipLive /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/:slug/overlay/:dueloId" element={<ChampionshipTenantProvider><ChampionshipOverlay /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/:slug/regulamento" element={<ChampionshipTenantProvider><ChampionshipRegulamento /></ChampionshipTenantProvider>} />
                  <Route path="/opencode/:slug/media-kit" element={<ChampionshipTenantProvider><ChampionshipMediaKit /></ChampionshipTenantProvider>} />

                  {/* Public NPS Survey */}
                  <Route path="/nps/:token" element={<PublicNPSSurvey />} />

                  {/* Lecture Registration */}
                  <Route path="/palestra/financeira" element={<LectureRegistration />} />
                  <Route path="/inscricoes-palestra" element={<AppLayout><ProtectedRoute><LectureRegistrations /></ProtectedRoute></AppLayout>} />
                  <Route path="/admin/palestra" element={<AppLayout><ProtectedRoute><LectureRegistrations /></ProtectedRoute></AppLayout>} />

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </OrganizationProvider>
      </PortalAuthProvider>
      </ModuleAccessProvider>
    </AuthProvider>
  </QueryClientProvider>
  </SentryErrorBoundary>
);

export default App;
